import {
  useEffect, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import './BulkImportForm.css';
import '@styles/base.css';
import { useDownloadTemplate } from '@utils/client-upload/template';
import * as events from '../../../utils/client-upload/csv/events';
import { FileInputForm } from './FileInputForm';
import { ImportProgressForm } from './ImportProgressForm';
import { ResultsForm } from './ResultsForm';
import { ProcessErrorForm } from './ProcessErrorForm';
import { useCSVFileUpload } from '@utils/client-upload/csv/hook';

export const BulkImportForm = ({ handleClose }) => {
  const [stage, setStage] = useState(1);
  const [filename, setFilename] = useState(null);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [downloadButtonState, setDownloadButtonState] = useState(false);
  const [uploadButtonState, setUploadButtonState] = useState('disabled');
  const [totalCount, setTotalCount] = useState(0);
  const [notification, setNotification] = useState({ message: '', type: 'standard' });
  const [showDelete, setShowDelete] = useState(false);

  const [
    saveFiles,
    createStreams,
    downloadFiles,
    cancelProcess,
  ] = useCSVFileUpload();
  const downloadTemplate = useDownloadTemplate();
  const handleProcessingCSVFileEvent = useCallback(
    (event) => {
      const { filename } = event.detail;
      setFilename(filename);
      setNotification({ message: 'Importing...', type: 'standard' });
    },
    []
  );
  const handleProcessedCSVRowEvent = useCallback(
    (event) => {
      const { success } = event.detail;
      if (success) {
        setSuccessCount((previous) => previous + 1);
        return;
      }
      setErrorCount((previous) => previous + 1);
    },
    []
  );
  const handleProcessingRowsEvent = useCallback(
    (event) => {
      setTotalCount(event.detail.numberOfRows);
    },
    []
  );
  const handleStartProcessingCSVFilesEvent = useCallback(
    () => {
      setDownloadButtonState('default');
    },
    []
  );
  const handleEndProcessingCSVFilesEvent = useCallback(
    () => {
      setNotification({ message: 'Import complete, preparing results', type: 'standard' });
      setStage((previous) => previous + 1);
    },
    []
  );
  const handleProcessingErrorEvent = useCallback(
    (event) => {
      const { error } = event.detail;
      setNotification({ message: error.message, type: 'error' });
      if (stage === 2) {
        setStage(4);
      }
    },
    [stage]
  );

  useEffect(
    () => {
      // Remove handlers if they're already set.
      window.removeEventListener(events.ProcessingCSVFileEvent.name, handleProcessingCSVFileEvent);
      window.removeEventListener(events.ProcessedCSVRowEvent.name, handleProcessedCSVRowEvent);
      window.removeEventListener(events.StartProcessingCSVFilesEvent.name, handleStartProcessingCSVFilesEvent);
      window.removeEventListener(events.EndProcessingCSVFilesEvent.name, handleEndProcessingCSVFilesEvent);
      window.removeEventListener(events.ProcessingRowsEvent.name, handleProcessingRowsEvent);
      window.removeEventListener(events.ProcessingErrorEvent.name, handleProcessingErrorEvent);

      //(Re)set the handlers.
      window.addEventListener(events.ProcessingCSVFileEvent.name, handleProcessingCSVFileEvent);
      window.addEventListener(events.ProcessedCSVRowEvent.name, handleProcessedCSVRowEvent);
      window.addEventListener(events.StartProcessingCSVFilesEvent.name, handleStartProcessingCSVFilesEvent);
      window.addEventListener(events.EndProcessingCSVFilesEvent.name, handleEndProcessingCSVFilesEvent);
      window.addEventListener(events.ProcessingRowsEvent.name, handleProcessingRowsEvent);
      window.addEventListener(events.ProcessingErrorEvent.name, handleProcessingErrorEvent);
      return () => {
        // Remove the handlers on unmount.
        window.removeEventListener(events.ProcessingCSVFileEvent.name, handleProcessingCSVFileEvent);
        window.removeEventListener(events.ProcessedCSVRowEvent.name, handleProcessedCSVRowEvent);
        window.removeEventListener(events.StartProcessingCSVFilesEvent.name, handleStartProcessingCSVFilesEvent);
        window.removeEventListener(events.EndProcessingCSVFilesEvent.name, handleEndProcessingCSVFilesEvent);
        window.removeEventListener(events.ProcessingRowsEvent.name, handleProcessingRowsEvent);
        window.removeEventListener(events.ProcessingErrorEvent.name, handleProcessingErrorEvent);
      };
    },
    [
      handleProcessingCSVFileEvent,
      handleProcessedCSVRowEvent,
      handleStartProcessingCSVFilesEvent,
      handleEndProcessingCSVFilesEvent,
      handleProcessingRowsEvent,
      handleProcessingErrorEvent,
    ]
  );
  return (
    <bulk-import-form>
      {stage === 1 && <FileInputForm
        handleCancel={handleClose}
        handleUpload={() => {
          setStage((previous) => previous + 1);
          createStreams();
        }}
        handleInputOnChange={async (event) => {
          const fileList = event.target.files;
          if (fileList instanceof FileList) {
            if (fileList.length > 0) {
              setNotification({ message: 'Inspecting file(s)', type: 'standard' });
              try {
                await saveFiles(event.target.files);
                setUploadButtonState('default');
                setNotification({});
                setShowDelete(true);
              } catch (error) {
                setNotification({ message: error.message, type: 'error' });
                setUploadButtonState('disabled');
                setShowDelete(false);
              }
              return;
            }
            setNotification({});
            setUploadButtonState('disabled');
            return;
          }
          setUploadButtonState('disabled');
          setNotification({});
          setShowDelete(true);
        }}
        handleDownload={() => downloadTemplate()}
        uploadButtonState={uploadButtonState}
        notification={notification}
        showDelete={showDelete}
      />}
      {stage === 2 && <ImportProgressForm
        handleCancelProcess={cancelProcess}
        filename={filename}
        rowsProcessed={successCount + errorCount}
        totalSuccessCount={successCount}
        totalErrorCount={errorCount}
        totalRowCount={totalCount}
        notification={notification}
      />}
      {stage === 3 && <ResultsForm
        handleDownloadFiles={downloadFiles}
        handleCloseModal={handleClose}
        totalSuccessCount={successCount}
        totalErrorCount={errorCount}
        totalRowCount={totalCount}
        filename={filename}
        downloadButtonState={downloadButtonState}
      />}
      {stage === 4 && <ProcessErrorForm
        message={notification}
        handleSubmitNewFile={() => {
          setNotification({});
          setStage(1);
        }}
      />
      }
    </bulk-import-form>
  );
};

BulkImportForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
};
